import React, { useState, useEffect } from "react";
import api, { formatApiError } from "@/api/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Eye, CheckCircle, Loader2, ShoppingCart, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const STATUS_MAP = {
  received: { label: "Vastaanotettu", cls: "bg-zinc-700/40 text-zinc-200" },
  approved: { label: "Hyväksytty", cls: "bg-blue-600/30 text-blue-200" },
  paid: { label: "Maksettu", cls: "bg-emerald-600/30 text-emerald-200" },
  rejected: { label: "Hylätty", cls: "bg-red-600/30 text-red-200" },
};

function PurchaseForm({ invoice, onSaved, onClose }) {
  const isEdit = !!invoice;
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    invoice_number: invoice?.invoice_number || "",
    supplier_name: invoice?.supplier_name || "",
    supplier_business_id: invoice?.supplier_business_id || "",
    supplier_email: invoice?.supplier_email || "",
    invoice_date: invoice?.invoice_date || new Date().toISOString().split("T")[0],
    due_date: invoice?.due_date || "",
    reference_number: invoice?.reference_number || "",
    net_amount: invoice?.net_amount || 0,
    vat_total: invoice?.vat_total || 0,
    total_amount: invoice?.total_amount || 0,
    status: invoice?.status || "received",
    notes: invoice?.notes || "",
    image_url: invoice?.image_url || "",
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.supplier_name) {
      toast.error("Toimittajan nimi puuttuu");
      return;
    }
    setLoading(true);
    try {
      const data = {
        ...form,
        net_amount: parseFloat(form.net_amount) || 0,
        vat_total: parseFloat(form.vat_total) || 0,
        total_amount: parseFloat(form.total_amount) || 0,
      };
      if (isEdit) await api.put(`/purchase-invoices/${invoice.id}`, data);
      else await api.post("/purchase-invoices", data);
      toast.success(isEdit ? "Päivitetty" : "Ostolasku lisätty");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  };

  return (
    <div className="space-y-4" data-testid="purchase-form">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="text-xs text-muted-foreground">Toimittajan nimi *</label>
          <Input value={form.supplier_name} onChange={set("supplier_name")} className="mt-1" data-testid="purchase-supplier" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Y-tunnus</label>
          <Input value={form.supplier_business_id} onChange={set("supplier_business_id")} className="mt-1" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Laskun numero</label>
          <Input value={form.invoice_number} onChange={set("invoice_number")} className="mt-1" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Laskupäivä</label>
          <Input type="date" value={form.invoice_date} onChange={set("invoice_date")} className="mt-1" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Eräpäivä</label>
          <Input type="date" value={form.due_date} onChange={set("due_date")} className="mt-1" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Veroton (€)</label>
          <Input type="number" step="0.01" value={form.net_amount} onChange={set("net_amount")} className="mt-1" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">ALV (€)</label>
          <Input type="number" step="0.01" value={form.vat_total} onChange={set("vat_total")} className="mt-1" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Yhteensä (€)</label>
          <Input type="number" step="0.01" value={form.total_amount} onChange={set("total_amount")} className="mt-1" data-testid="purchase-total" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Viitenumero</label>
          <Input value={form.reference_number} onChange={set("reference_number")} className="mt-1" />
        </div>
        <div className="col-span-2">
          <label className="text-xs text-muted-foreground">Huomiot</label>
          <Input value={form.notes} onChange={set("notes")} className="mt-1" />
        </div>
      </div>
      <div className="flex gap-2">
        <Button variant="outline" onClick={onClose} className="flex-1">
          Peruuta
        </Button>
        <Button onClick={save} disabled={loading} className="flex-1 gap-2 bg-amber-500 hover:bg-amber-400 text-zinc-900" data-testid="purchase-save">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
          {isEdit ? "Tallenna" : "Lisää ostolasku"}
        </Button>
      </div>
    </div>
  );
}

export default function PurchaseInvoicesPanel({ onItems }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/purchase-invoices");
      setItems(r.data);
      onItems && onItems(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Poistetaanko?")) return;
    try {
      await api.delete(`/purchase-invoices/${id}`);
      toast.success("Poistettu");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const markPaid = async (id) => {
    try {
      await api.patch(`/purchase-invoices/${id}/status`, null, { params: { status: "paid" } });
      toast.success("Merkitty maksetuksi");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const today = new Date().toISOString().split("T")[0];
  const overdueCount = items.filter((p) => p.due_date && p.due_date < today && !["paid", "rejected"].includes(p.status)).length;
  const totalUnpaid = items
    .filter((p) => !["paid", "rejected"].includes(p.status))
    .reduce((s, p) => s + (Number(p.total_amount) || 0), 0);

  return (
    <div className="space-y-4" data-testid="purchase-invoices-panel">
      <div className="grid grid-cols-3 gap-3">
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-xl font-bold text-blue-400">{items.length}</p>
            <p className="text-xs text-muted-foreground">Ostolaskuja</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-xl font-bold text-amber-400">{totalUnpaid.toFixed(0)} €</p>
            <p className="text-xs text-muted-foreground">Maksamatta</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <p className={`text-xl font-bold ${overdueCount > 0 ? "text-red-400" : "text-emerald-400"}`}>
              {overdueCount}
            </p>
            <p className="text-xs text-muted-foreground">Erääntyneet</p>
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-end">
        <Button
          size="sm"
          className="gap-2 bg-amber-500 hover:bg-amber-400 text-zinc-900"
          onClick={() => {
            setEditItem(null);
            setShowForm(true);
          }}
          data-testid="purchase-new"
        >
          <Plus className="w-4 h-4" /> Lisää ostolasku
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Ladataan...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-20" />
          <p>Ei ostolaskuja. Lisää ensimmäinen ostolasku!</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((p) => {
            const st = STATUS_MAP[p.status] || STATUS_MAP.received;
            const isOverdue = p.due_date && p.due_date < today && !["paid", "rejected"].includes(p.status);
            return (
              <Card key={p.id} className={isOverdue ? "border-red-500/30" : ""} data-testid={`purchase-row-${p.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-sm">{p.supplier_name}</span>
                        <Badge className={`text-[10px] ${st.cls}`}>{st.label}</Badge>
                        {isOverdue && <Badge variant="destructive" className="text-[10px]">Erääntynyt!</Badge>}
                      </div>
                      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                        {p.invoice_number && <span>Nro: {p.invoice_number}</span>}
                        <span>Eräpäivä: {p.due_date}</span>
                        <span className="font-semibold text-foreground">{(Number(p.total_amount) || 0).toFixed(2)} €</span>
                      </div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      {p.image_url && (
                        <a href={p.image_url} target="_blank" rel="noopener noreferrer">
                          <Button size="icon" variant="ghost" className="w-7 h-7">
                            <Eye className="w-3 h-3" />
                          </Button>
                        </a>
                      )}
                      {p.status === "received" && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="w-7 h-7 text-emerald-400"
                          title="Merkitse maksetuksi"
                          onClick={() => markPaid(p.id)}
                          data-testid={`purchase-mark-paid-${p.id}`}
                        >
                          <CheckCircle className="w-3 h-3" />
                        </Button>
                      )}
                      <Button
                        size="icon"
                        variant="ghost"
                        className="w-7 h-7"
                        onClick={() => {
                          setEditItem(p);
                          setShowForm(true);
                        }}
                      >
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="w-7 h-7 text-destructive/60 hover:text-destructive"
                        onClick={() => remove(p.id)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg" data-testid="purchase-dialog">
          <DialogHeader>
            <DialogTitle>{editItem ? "Muokkaa ostolaskua" : "Lisää ostolasku"}</DialogTitle>
          </DialogHeader>
          <PurchaseForm
            invoice={editItem}
            onSaved={() => {
              setShowForm(false);
              load();
            }}
            onClose={() => setShowForm(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
